import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { safeStorage } from '@/lib/safeStorage';
import { translations, type Language, type Translations } from '@/translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'tripboom-language';

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const saved = safeStorage.getItem(STORAGE_KEY);
      if (saved && (saved === 'en' || saved === 'fr' || saved === 'es' || saved === 'de')) {
        return saved as Language;
      }
      // Detect browser language for first-time users
      const browserLang = navigator.language?.slice(0, 2)?.toLowerCase();
      if (browserLang === 'fr' || browserLang === 'es' || browserLang === 'de') {
        return browserLang as Language;
      }
    }
    return 'en';
  });

  useEffect(() => {
    safeStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
